import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../api/Axios";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { formatDateTimeLocal } from "../../utils/dateUtils";
import { API_ENDPOINTS } from "../../constants/api";
import { formatHours, roundToOneDecimal } from "../../utils/numberUtils";

const DeveloperSummaryModal = ({ isOpen, onClose, tasks, developers, projects }) => {
  const [summary, setSummary] = useState({});
  const [selectedDeveloper, setSelectedDeveloper] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const generateSummary = useCallback(() => {
    const summaryData = {};

    // Проверяем, что у нас есть данные
    if (!developers || !tasks) {
      setSummary({});
      return;
    }

    developers.forEach(developer => {
      let developerTasks = [];
      
      // Специальная обработка для "Все разработчики"
      if (developer.id === 'all') {
        // Для "Все разработчики" собираем ВСЕ задачи
        Object.values(tasks).forEach(categoryTasks => {
          if (Array.isArray(categoryTasks)) {
            developerTasks.push(...categoryTasks);
          }
        });
      } else {
        // Для конкретного разработчика собираем только его задачи
        Object.values(tasks).forEach(categoryTasks => {
          if (Array.isArray(categoryTasks)) {
            categoryTasks.forEach(task => {
              if (task.developer === developer.id) {
                developerTasks.push(task);
              }
            });
          }
        });
      }

      // Filter completed tasks (items3 category)
      const completedTasks = developerTasks.filter(task => 
        task.category === 'items3'
      );

      // Calculate total hours for all tasks
      const totalHours = roundToOneDecimal(developerTasks.reduce((sum, task) => sum + (Number(task.hours) || 0), 0));
      
      // Calculate hours for completed tasks only
      const completedHours = roundToOneDecimal(completedTasks.reduce((sum, task) => sum + (Number(task.hours) || 0), 0));
      
      const totalTasks = developerTasks.length;
      const completedTasksCount = completedTasks.length;

      summaryData[developer.id] = {
        name: developer.name,
        totalTasks,
        completedTasksCount,
        totalHours,
        completedHours,
        completedTasks
      };
    });

    setSummary(summaryData);
  }, [developers, tasks]);

  useEffect(() => {
    if (isOpen && tasks && developers) {
      generateSummary();
    }
  }, [isOpen, tasks, developers, generateSummary]);

  // Сортируем разработчиков по общему времени (от большего к меньшему)
  const sortedSummary = Object.entries(summary)
    .filter(([developerId]) => developerId !== 'all') // Убираем "Все разработчики"
    .sort((a, b) => {
      const aHasTasks = a[1].totalHours > 0;
      const bHasTasks = b[1].totalHours > 0;
      if (aHasTasks && !bHasTasks) return -1;
      if (!aHasTasks && bHasTasks) return 1;
      if (aHasTasks && bHasTasks) {
        return b[1].totalHours - a[1].totalHours;
      }
      return a[1].name.localeCompare(b[1].name, 'ru');
    });

  // Подсчитываем общую статистику
  // Используем исходные данные tasks для корректного подсчета
  const allTasks = Object.values(tasks).flat();
  const uniqueTaskIds = new Set();
  const uniqueCompletedTaskIds = new Set();
  let totalUniqueHours = 0;
  let totalUniqueCompletedHours = 0;
  
  allTasks.forEach(task => {
    if (!uniqueTaskIds.has(task.id)) {
      uniqueTaskIds.add(task.id);
      totalUniqueHours += Number(task.hours) || 0;
      
      if (task.category === 'items3') {
        uniqueCompletedTaskIds.add(task.id);
        totalUniqueCompletedHours += Number(task.hours) || 0;
      }
    }
  });

  const totalStats = {
    totalHours: roundToOneDecimal(totalUniqueHours),
    completedHours: roundToOneDecimal(totalUniqueCompletedHours),
    totalTasks: uniqueTaskIds.size,
    completedTasks: uniqueCompletedTaskIds.size
  };

  const handleExport = async () => {
    if (!selectedDeveloper || !summary[selectedDeveloper]) return;

    setIsLoading(true);
    try {
      const developerData = summary[selectedDeveloper];
      const monthYear = new Date().toLocaleDateString("ru-RU", {
        year: "numeric",
        month: "long"
      });

      // Generate report data
      const reportData = {
        developerName: developerData.name,
        monthYear,
        totalHours: developerData.totalHours,
        completedHours: developerData.completedHours,
        tasks: developerData.completedTasks.map(task => ({
          title: task.title,
          description: task.description,
          projectName: getProjectName(task.project),
          hours: task.hours,
                  createdAt: formatDateTimeLocal(task.createdAt),
        completedAt: task.completedAt ? formatDateTimeLocal(task.completedAt) : "N/A"
        }))
      };

      // Send to backend for report generation
      const response = await axiosInstance.post(API_ENDPOINTS.SEND_TO_TELEGRAM, reportData);

      // For now, just show success message
      console.log('Report generated successfully:', response.data);
      alert(`Отчет для ${developerData.name} успешно сгенерирован!`);

    } catch (error) {
      console.error("Error exporting report:", error);
      alert('Ошибка при генерации отчета');
    } finally {
      setIsLoading(false);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects?.find(p => p.id === projectId);
    return project?.name || "Неизвестный проект";
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Сводка по разработчикам"
      size="xlarge"
    >
      <div className="space-y-4">
        {/* Общая статистика */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-3">📊 Общая статистика</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-600">Всего задач:</span>
              <p className="font-semibold text-blue-800">{totalStats.totalTasks}</p>
            </div>
            <div>
              <span className="text-blue-600">Завершено задач:</span>
              <p className="font-semibold text-green-600">{totalStats.completedTasks}</p>
            </div>
            <div>
              <span className="text-blue-600">Общее время:</span>
              <p className="font-semibold text-blue-800">{formatHours(totalStats.totalHours)} ч</p>
            </div>
            <div>
              <span className="text-blue-600">Завершено:</span>
              <p className="font-semibold text-green-600">{formatHours(totalStats.completedHours)} ч</p>
            </div>
          </div>
          {totalStats.totalHours > 0 && (
            <div className="mt-2 text-xs text-blue-600">
              Общий прогресс: {Math.round((totalStats.completedHours / totalStats.totalHours) * 100)}%
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedSummary.map(([developerId, data]) => (
            <div
              key={developerId}
              className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                selectedDeveloper === developerId
                  ? 'border-blue-500 bg-blue-50'
                  : data.totalHours > 0 
                    ? 'border-gray-200 hover:border-gray-300'
                    : 'border-gray-100 bg-gray-50'
              }`}
              onClick={() => setSelectedDeveloper(developerId)}
            >
              <h3 className="font-semibold text-gray-800">{data.name}</h3>
              <div className="text-sm text-gray-600 mt-2 space-y-1">
                <p>Всего задач: {data.totalTasks}</p>
                <p>Завершено задач: {data.completedTasksCount}</p>
                <p className="font-medium text-blue-600">Общее время: {formatHours(data.totalHours)} ч</p>
                <p className="font-medium text-green-600">Завершено: {formatHours(data.completedHours)} ч</p>
                {data.totalHours > 0 ? (
                  <p className="text-xs text-gray-500">
                    Прогресс: {Math.round((data.completedHours / data.totalHours) * 100)}%
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">Нет активных задач</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {selectedDeveloper && summary[selectedDeveloper] && (
          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3">
              Детали: {summary[selectedDeveloper].name}
            </h4>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Всего задач:</span>
                  <p className="font-semibold">{summary[selectedDeveloper].totalTasks}</p>
                </div>
                <div>
                  <span className="text-gray-600">Завершено задач:</span>
                  <p className="font-semibold text-green-600">{summary[selectedDeveloper].completedTasksCount}</p>
                </div>
                <div>
                  <span className="text-gray-600">Общее время:</span>
                  <p className="font-semibold text-blue-600">{formatHours(summary[selectedDeveloper].totalHours)} ч</p>
                </div>
                <div>
                  <span className="text-gray-600">Завершено:</span>
                  <p className="font-semibold text-green-600">{formatHours(summary[selectedDeveloper].completedHours)} ч</p>
                </div>
              </div>
            </div>
            {summary[selectedDeveloper].completedTasks.length > 0 ? (
              <div className="max-h-60 overflow-y-auto">
                {summary[selectedDeveloper].completedTasks.map((task, index) => (
                  <div key={index} className="p-3 border rounded mb-2">
                    <h5 className="font-medium">{task.title}</h5>
                    <p className="text-sm text-gray-600">{task.description}</p>
                    <div className="text-xs text-gray-500 mt-1">
                      <span>Проект: {getProjectName(task.project)}</span>
                      <span className="mx-2">•</span>
                      <span>Часы: {formatHours(task.hours)}</span>
                      <span className="mx-2">•</span>
                                              <span>Завершено: {formatDateTimeLocal(task.completedAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>У этого разработчика нет завершенных задач</p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Закрыть
          </Button>
          <Button
            onClick={handleExport}
            disabled={!selectedDeveloper || isLoading}
          >
            {isLoading ? "Экспорт..." : "Экспорт отчета"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeveloperSummaryModal; 